import { Button, Checkbox, Input, Toggle } from "@jutro/ui";
import { Controller, useForm } from "react-hook-form";
import {
  EdmGetAllStaffWithGenreAndWorkingHoursDocument,
  EdmLanguage,
  EdmMedicalPerson,
  useEdmUpdatePreferencesMutation,
} from "lib/graphql/megaSchema";
import { toaster } from "lib/tools/toaster";
import { supportedLanguagesOptions } from "views/StaffSchedule/StaffSettings/components/StaffSettingsPanel/tools";

type Props = {
  selectedStaff: EdmMedicalPerson;
};

export const StaffPreferencesForm = ({ selectedStaff }: Props) => {
  const { acceptAdults, acceptChildren, preferences, languages } =
    selectedStaff;

  const defaultValues = {
    adultsAvailable: acceptAdults,
    childrenAvailable: acceptChildren,
    minAllowedChildAge: preferences?.minAllowedChildAge?.toString() ?? "",
    languages: (languages ?? []) as EdmLanguage[],
  };

  const {
    control,
    formState: { isDirty },
    watch,
    reset,
    handleSubmit,
  } = useForm({
    defaultValues,
  });

  const { childrenAvailable } = watch();

  const [updatePreferences, { loading }] = useEdmUpdatePreferencesMutation({
    refetchQueries: [EdmGetAllStaffWithGenreAndWorkingHoursDocument],
  });

  const onSubmit = async (data: typeof defaultValues) => {
    if (!isDirty) {
      return;
    }

    const input = {
      ...data,
      minAllowedChildAge: data.minAllowedChildAge
        ? Number(data.minAllowedChildAge)
        : 0,
    };

    await toaster.promise(
      updatePreferences({
        variables: { staffId: selectedStaff.id, input },
      }),
      {
        error: "Nie udało się zaktualizować ustawień obsługi pacjentów",
        loading: "Aktualizuję ustawienia obsługi pacjentów",
        success: "Zaktualizowano ustawienia obsługi pacjentów",
      },
    );

    reset(data);
  };

  return (
    <div className="flex h-[544px] flex-col gap-8 p-4">
      <p className="font-paragraph-1">
        Zmieniając ustawienia poniżej możesz ograniczyć, kto może się zapisać na
        wizytę u tego pracownika.
      </p>

      <div className="flex flex-col gap-6">
        <Controller
          control={control}
          name="adultsAvailable"
          render={({ field: { value, onChange } }) => (
            <Toggle
              label="Przyjmuje dorosłych"
              isEnabled={value}
              setIsEnabled={onChange}
            />
          )}
        />

        <Controller
          control={control}
          name="childrenAvailable"
          render={({ field: { value, onChange } }) => (
            <Toggle
              label="Przyjmuje dzieci"
              isEnabled={value}
              setIsEnabled={onChange}
            />
          )}
        />

        {childrenAvailable && (
          <Controller
            name="minAllowedChildAge"
            control={control}
            render={({ field: { value, onChange } }) => (
              <div className="w-[189px]">
                <Input
                  label="Minimalny wiek pacjenta"
                  type="number"
                  max={18}
                  sideElement="lat"
                  sideElementPosition="end"
                  placeholder="0"
                  value={value}
                  onChange={(e) => {
                    if (e.target.value === "") {
                      return onChange(null);
                    }

                    if (Number(e.target.value) > 18) {
                      return;
                    }

                    onChange(Number(e.target.value));
                  }}
                />
              </div>
            )}
          />
        )}
      </div>

      <Controller
        control={control}
        name="languages"
        render={({ field: { value, onChange } }) => (
          <div className="flex flex-col gap-2">
            <label className="font-label">Język</label>
            {supportedLanguagesOptions.map((option) => (
              <Checkbox
                key={option.value}
                label={option.label}
                name={option.value}
                checked={value.includes(option.value)}
                onChange={(e) => {
                  const isChecked = e.target.checked;

                  if (isChecked) {
                    return onChange([...value, option.value]);
                  }

                  onChange(value.filter((lang) => lang !== option.value));
                }}
              />
            ))}
          </div>
        )}
      />

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="secondary"
          text="Anuluj"
          full={false}
          disabled={loading}
          onClick={() => reset(defaultValues)}
        />

        <Button
          type="button"
          variant="primary"
          text="Zapisz"
          full={false}
          loading={loading}
          onClick={handleSubmit((data) => onSubmit(data))}
        />
      </div>
    </div>
  );
};
